<template>
  <app-content style="position: relative;">
    <div class="mask" v-if="User.role =='View'"></div>
    <div class="professional-form">
      <div class="form-title">{{ isEdit ? '编辑票据' : '新增票据' }}</div>

      <Form ref="form" :labelWidth="0" mode="block" :rules="rules" :model="form">
        <!-- 文件上传区域 -->
        <div class="form-section">
          <div class="section-title">附件上传</div>
          <div class="section-content grid-full">
            <FileUpload
              v-model="form.attachmentPath"
              :fileName.sync="uploadedFileName"
              :disabled="User.role =='View'"
              uploadApi="bill"
              :enableRecognition="true"
              recognitionApi="/upload/smart-recognize-ticket"
              @recognition-result="handleRecognitionResult"
              label="附件文件"
              prop="attachmentPath"
            />
          </div>
        </div>
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title">基本信息</div>
          <div class="section-content grid-3">
            <div class="form-item readonly-field">
              <label class="form-label">票据编号</label>
              <input type="text" v-model="form.billNo" placeholder="系统自动生成" readonly>
            </div>
            <div class="form-item">
              <label class="form-label">发票号码</label>
              <input type="text" v-model="form.invoiceNumber" placeholder="请输入发票号码（可为空）" :disabled="User.role =='View'">
            </div>
            <div class="form-item important-field">
              <label class="form-label required">开票日期</label>
              <input type="date" v-model="form.billDate" :disabled="User.role =='View'">
            </div>
          </div>
        </div>

        <!-- 主体信息 -->
        <div class="form-section">
          <div class="section-title">主体信息</div>
          <div class="section-content grid-2">
            <div class="form-item important-field">
              <label class="form-label required">开票方</label>
              <input type="text" v-model="form.issuer" placeholder="请输入开票方" :disabled="User.role =='View'">
            </div>
            <div class="form-item">
              <label class="form-label">收票方</label>
              <input type="text" v-model="form.recipient" placeholder="请输入收票方" :disabled="User.role =='View'">
            </div>
          </div>
        </div>

        <!-- 金额信息 -->
        <div class="form-section">
          <div class="section-title">金额信息</div>
          <div class="section-content grid-3">
            <div class="form-item important-field">
              <label class="form-label required">合计金额</label>
              <div class="amount-input">
                <span class="currency-symbol">¥</span>
                <input type="number" v-model="form.amount" placeholder="0.00" :disabled="User.role =='View'" step="0.01" min="0">
              </div>
            </div>
            <div class="form-item">
              <label class="form-label">税率</label>
              <input type="number" v-model="form.taxRate" placeholder="请输入税率(%)" :disabled="User.role =='View'" step="0.01" min="0">
            </div>
            <div class="form-item">
              <label class="form-label">合计税额</label>
              <div class="amount-input">
                <span class="currency-symbol">¥</span>
                <input type="number" v-model="form.totalTaxAmount" placeholder="0.00" :disabled="User.role =='View'" step="0.01" min="0">
              </div>
            </div>
          </div>
        </div>

        <!-- 分类信息 -->
        <div class="form-section">
          <div class="section-title">分类信息</div>
          <div class="section-content grid-2">
            <div class="form-item important-field">
              <label class="form-label required">票据类型</label>
              <select v-model="form.type" :disabled="User.role =='View'">
                <option value="">请选择票据类型</option>
                <option v-for="option in typeOptions" :key="option.key" :value="option.key">{{ option.title }}</option>
              </select>
            </div>
            <!-- 状态字段已隐藏，但保留在数据模型中以备将来扩展 -->
            <div class="form-item">
              <label class="form-label">大写金额</label>
              <input type="text" v-model="form.amountInWords" placeholder="请输入大写金额" :disabled="User.role =='View'">
            </div>
          </div>
        </div>

        <!-- 详细描述 -->
        <div class="form-section">
          <div class="section-title">详细描述</div>
          <div class="section-content grid-full">
            <div class="form-item important-field">
              <label class="form-label required">摘要</label>
              <textarea v-model="form.summary" placeholder="请输入摘要" :disabled="User.role =='View'"></textarea>
            </div>
            <div class="form-item">
              <label class="form-label">备注</label>
              <textarea v-model="form.remark" placeholder="请输入备注" :disabled="User.role =='View'"></textarea>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="form-actions">
          <button v-if="User.role !='View'" class="btn btn-primary" :disabled="saving" @click="save">
            {{ saving ? '保存中...' : '保存' }}
          </button>
          <button class="btn" @click="goBack">返回</button>
        </div>
      </Form>
    </div>
  </app-content>
</template>

<script>
import moment from 'moment'
import FileUpload from '@/components/FileUpload.vue'
import formMixin from '@/mixins/formMixin.js'

export default {
  name: "BillForm",
  components: {
    FileUpload
  },
  mixins: [formMixin],
  data() {
    return {
      form: {
        id: null,
        billNo: '', // 票据编号 (系统生成)
        billDate: moment().format('YYYY-MM-DD'), // 开票日期
        type: '增值税专用发票', // 票据类型
        invoiceNumber: '', // 发票号码
        amount: 0, // 合计金额 (Double类型)
        taxRate: 0, // 税率 (前端数字，后端BigDecimal)
        totalTaxAmount: 0, // 合计税额 (前端数字，后端BigDecimal)
        amountInWords: '', // 小写金额
        issuer: '', // 开票方
        recipient: '', // 收票方
        summary: '', // 摘要
        remark: '', // 备注
        status: '未使用', // 状态
        // 关联凭证功能已移至关联管理模块
        attachmentPath: '', // 附件路径
        ocrRecognitionInfo: '' // OCR识别信息
      },
      uploadedFileName: '',
      typeOptions: [
        {title: '增值税专用发票', key: '增值税专用发票'},
        {title: '增值税普通发票', key: '增值税普通发票'},
        {title: '电子发票(专用发票)', key: '电子发票(专用发票)'},
        {title: '电子发票(普通发票)', key: '电子发票(普通发票)'},
        {title: '火车票', key: '火车票'},
        {title: '机票行程单', key: '机票行程单'},
        {title: '出租车票', key: '出租车票'},
        {title: '收据', key: '收据'},
        {title: '其他', key: '其他'}
      ],
      // 状态选项已隐藏，但保留数据以备将来扩展
      // statusOptions: [
      //   {title: '未使用', key: '未使用'},
      //   {title: '已使用', key: '已使用'},
      //   {title: '已作废', key: '已作废'}
      // ],
      rules: {
        billDate: { required: true, message: '请选择开票日期' },
        type: { required: true, message: '请选择票据类型' },
        amount: { required: true, message: '请输入金额' },
        issuer: { required: true, message: '请输入开票方' },
        summary: { required: true, message: '请输入摘要' }
      }
    }
  },
  computed: {
    billId() {
      return this.$route.params.billId
    }
  },
  created() {
    if (this.billId) {
      this.loadData()
    } else {
      this.loadBillNo()
    }
  },
  methods: {
    loadData() {
      this.loading = true
      this.$api.bill.load(this.billId).then(({data}) => {
        console.log('加载的票据数据:', data) // 添加调试信息
        this.form = {
          ...data,
          billDate: this.formatDate(data.billDate),
          // 确保数值字段正确转换
          amount: data.amount || 0,
          // 后端BigDecimal字段转换为前端数字类型
          taxRate: data.taxRate ? parseFloat(data.taxRate) : 0,
          totalTaxAmount: data.totalTaxAmount ? parseFloat(data.totalTaxAmount) : 0,
          amountInWords: data.amountInWords || '',
          ocrRecognitionInfo: data.ocrRecognitionInfo || ''
        }
        console.log('设置后的表单数据:', this.form) // 添加调试信息
        this.uploadedFileName = data.attachmentPath ? data.attachmentPath.split('/').pop() : ''
      }).finally(() => {
        this.loading = false
      })
    },
    
    loadBillNo() {
      const date = moment()
      this.$api.bill.loadBillNo({
        year: date.year(),
        month: date.month() + 1
      }).then(({data}) => {
        this.form.billNo = data
      }).catch(error => {
        console.error('获取票据编号失败:', error)
      })
    },
    
    save() {
      this.saveForm(
        this.$api.bill,
        '/bills/bill-list',
        () => {
          // 保存前处理日期格式
          if (this.form.billDate) {
            this.form.billDate = this.formatDate(this.form.billDate)
          }
          // 确保数值字段正确转换 - 注意后端taxRate和totalTaxAmount是BigDecimal类型
          this.form.amount = parseFloat(this.form.amount) || 0

          // 处理税率：转换为字符串格式，后端会自动转换为BigDecimal
          if (this.form.taxRate !== null && this.form.taxRate !== undefined && this.form.taxRate !== '') {
            let tr = String(this.form.taxRate)
            // 去除百分号
            tr = tr.replace('%', '')
            let val = parseFloat(tr)
            if (!isNaN(val)) {
              // 若明显为百分比(>1)，按百分比转为小数
              if (val > 1) val = val / 100
              // 统一到4位小数
              this.form.taxRate = (Math.round(val * 10000) / 10000).toString()
            } else {
              this.form.taxRate = '0'
            }
          } else {
            this.form.taxRate = "0"
          }

          // 处理合计税额：转换为字符串格式，后端会自动转换为BigDecimal
          if (this.form.totalTaxAmount !== null && this.form.totalTaxAmount !== undefined && this.form.totalTaxAmount !== '') {
            this.form.totalTaxAmount = String(parseFloat(this.form.totalTaxAmount) || 0)
          } else {
            this.form.totalTaxAmount = "0"
          }

          console.log('保存前的表单数据:', this.form) // 添加调试信息
        }
      )
    },
    
    handleRecognitionResult(data) {
      console.log('票据OCR识别结果:', data) // 添加调试信息

      // 保存完整的OCR识别信息（优先使用易读格式）
      if (data.ocrRecognitionInfo) {
        this.form.ocrRecognitionInfo = data.ocrRecognitionInfo
        console.log('保存易读格式OCR识别信息:', this.form.ocrRecognitionInfo)
      } else if (data.ocrRecognitionInfoJson) {
        // 如果没有易读格式，使用JSON格式
        this.form.ocrRecognitionInfo = data.ocrRecognitionInfoJson
        console.log('保存JSON格式OCR识别信息:', this.form.ocrRecognitionInfo)
      } else if (data.rawOcrData) {
        // 如果都没有，但有原始数据，则保存原始数据
        this.form.ocrRecognitionInfo = JSON.stringify(data.rawOcrData, null, 2)
        console.log('保存原始OCR数据:', this.form.ocrRecognitionInfo)
      }

      // 创建一个新的表单对象来触发响应式更新
      const updatedForm = { ...this.form }

      // 如果有ocrRecognitionInfo文本，尝试从中解析数据
      if (data.ocrRecognitionInfo) {
        const parsedData = this.parseOcrRecognitionInfo(data.ocrRecognitionInfo)
        console.log('从OCR识别信息中解析的数据:', parsedData)

        // 合并解析的数据到data对象中
        Object.assign(data, parsedData)
      }

      // 处理发票号码（支持新旧字段名）
      if (data.invoice_number || data.invoiceNumber) {
        updatedForm.invoiceNumber = String(data.invoice_number || data.invoiceNumber).trim()
        console.log('更新发票号码:', updatedForm.invoiceNumber)
      }

      // 处理OCR识别结果 - 支持新的字段映射格式
      // 处理金额字段（支持新旧字段名）
      if (data.amount || data.totalAmount || data.amountInFigures) {
        const amount = data.amount || data.totalAmount || data.amountInFigures
        updatedForm.amount = parseFloat(String(amount).replace(/[^\d.]/g, '')) || 0
        console.log('更新合计金额:', updatedForm.amount)
      }

      // 处理税率 - 保存为数字类型，保存时会转换为字符串
      if (data.tax_rate || data.taxRate) {
        const taxRate = data.tax_rate || data.taxRate
        updatedForm.taxRate = parseFloat(String(taxRate).replace(/[^\d.]/g, '')) || 0
        console.log('更新税率:', updatedForm.taxRate)
      }

      // 处理合计税额 - 保存为数字类型，保存时会转换为字符串
      if (data.total_tax_amount || data.totalTaxAmount || data.taxAmount) {
        const taxAmount = data.total_tax_amount || data.totalTaxAmount || data.taxAmount
        updatedForm.totalTaxAmount = parseFloat(String(taxAmount).replace(/[^\d.]/g, '')) || 0
        console.log('更新合计税额:', updatedForm.totalTaxAmount)
      }

      // 处理大写金额
      if (data.amount_in_words || data.amountInWords || data.amountInFigures) {
        const amountWords = data.amount_in_words || data.amountInWords || data.amountInFigures
        updatedForm.amountInWords = String(amountWords).trim()
        console.log('更新大写金额:', updatedForm.amountInWords)
      }

      // 处理开票方（支持新旧字段名）
      if (data.issuer || data.sellerName) {
        updatedForm.issuer = String(data.issuer || data.sellerName).trim()
        console.log('更新开票方:', updatedForm.issuer)
      }

      // 处理收票方（支持新旧字段名）
      if (data.recipient || data.buyerName) {
        updatedForm.recipient = String(data.recipient || data.buyerName).trim()
        console.log('更新收票方:', updatedForm.recipient)
      }

      // 处理摘要（支持新旧字段名）
      if (data.summary || data.itemName) {
        updatedForm.summary = String(data.summary || data.itemName).trim()
        console.log('更新摘要:', updatedForm.summary)
      }

      // 处理开票日期（支持新旧字段名，多种命名回退）
      const dateCandidates = [
        data.bill_date,
        data.invoice_date,
        data.invoiceDate,
        data.billDate,
        data.date,
        // 从原始OCR文本解析时 parseOcrRecognitionInfo 已写入 parsedData.billDate
        data.billDate || (data.billDate && data.billDate.billDate)
      ].filter(Boolean)

      if (dateCandidates.length > 0) {
        const dateValue = dateCandidates[0]
        console.log('原始日期值:', dateValue)

        // 确保日期格式正确转换
        let formattedDate = this.formatDate(dateValue)

        // 如果formatDate返回null或无效值，尝试手动解析中文日期格式
        if (!formattedDate || formattedDate === 'Invalid date') {
          if (typeof dateValue === 'string' && dateValue.includes('年') && dateValue.includes('月') && dateValue.includes('日')) {
            const match = dateValue.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
            if (match) {
              const [, year, month, day] = match
              formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            }
          }
        }

        if (formattedDate && formattedDate !== 'Invalid date') {
          updatedForm.billDate = formattedDate
          console.log('更新日期: 原始值=', dateValue, ', 格式化后=', formattedDate)
        } else {
          console.warn('无法解析日期格式:', dateValue)
        }
      }

      // 处理票据类型（支持更多字段名与文本识别回退）
      if (data.type || data.invoiceType || data.invoice_type || data.ticket_type) {
        const originalType = String(data.type || data.invoiceType || data.invoice_type || data.ticket_type)
        const standardizedType = this.standardizeInvoiceType(originalType)
        if (standardizedType) {
          updatedForm.type = standardizedType
          console.log('原始类型:', originalType, '-> 标准化类型:', standardizedType)
        }
      }

      // 若仍未识别到类型，尝试从OCR可读文本中解析类型关键词
      if (!updatedForm.type && data.ocrRecognitionInfo) {
        const txt = String(data.ocrRecognitionInfo)
        const isElectronic = /电子发票/.test(txt)
        const isVat = /增值税/.test(txt)
        const isSpecial = /(专用发票|专票)/.test(txt)
        const isNormal = /(普通发票|普票)/.test(txt)
        if (isElectronic && isSpecial) {
          updatedForm.type = '电子发票(专用发票)'
        } else if (isElectronic && isNormal) {
          updatedForm.type = '电子发票(普通发票)'
        } else if (isVat && isSpecial) {
          updatedForm.type = '增值税专用发票'
        } else if (isVat && isNormal) {
          updatedForm.type = '增值税普通发票'
        } else if (isSpecial) {
          updatedForm.type = '增值税专用发票'
        } else if (isNormal) {
          updatedForm.type = '增值税普通发票'
        }
        if (updatedForm.type) {
          console.log('从OCR文本推断类型:', updatedForm.type)
        }
      }

      // 处理火车票特有字段
      if (data.train_number || data.departure_station || data.arrival_station) {
        updatedForm.type = '火车票'
        if (data.train_number) {
          updatedForm.summary = `${data.train_number} ${data.departure_station || ''}-${data.arrival_station || ''}`
        }
        console.log('识别为火车票，更新摘要:', updatedForm.summary)
      }

      // 处理机票特有字段
      if (data.flight_number || data.departure_city || data.arrival_city) {
        updatedForm.type = '机票行程单'
        if (data.flight_number) {
          updatedForm.summary = `${data.flight_number} ${data.departure_city || ''}-${data.arrival_city || ''}`
        }
        console.log('识别为机票行程单，更新摘要:', updatedForm.summary)
      }

      // 处理出租车票特有字段
      if (data.plate_number || data.get_on_time) {
        updatedForm.type = '出租车票'
        if (data.plate_number) {
          updatedForm.summary = `出租车 ${data.plate_number}`
        }
        console.log('识别为出租车票，更新摘要:', updatedForm.summary)
      }

      // 直接更新表单数据，原生HTML元素会自动响应
      this.form = updatedForm

      // 显示成功提示
      this.$Message.success('OCR识别成功，表单已自动填充')
      console.log('OCR识别完成，表单数据已更新:', this.form)
    },

    // 解析OCR识别信息文本，提取结构化数据
    parseOcrRecognitionInfo(ocrText) {
      const parsedData = {}

      if (!ocrText) return parsedData

      try {
        // 解析发票类型
        const typeMatch = ocrText.match(/发票类型\s*:\s*(.+)/)
        if (typeMatch) {
          parsedData.type = typeMatch[1].trim()
        }

        // 解析发票代码/发票号码
        const invoiceCodeMatch = ocrText.match(/发票代码\s*:\s*(\S+)/)
        if (invoiceCodeMatch) {
          parsedData.invoiceNumber = invoiceCodeMatch[1].trim()
        }

        // 解析开票日期
        const billDateMatch = ocrText.match(/开票日期\s*:\s*(\d{4}年\d{1,2}月\d{1,2}日)/)
        if (billDateMatch) {
          // 转换日期格式：2025年01月16日 -> 2025-01-16
          const dateStr = billDateMatch[1]
          const dateMatch = dateStr.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
          if (dateMatch) {
            const year = dateMatch[1]
            const month = dateMatch[2].padStart(2, '0')
            const day = dateMatch[3].padStart(2, '0')
            parsedData.billDate = `${year}-${month}-${day}`
          }
        }

        // 解析销售方名称（开票方）
        const issuerMatch = ocrText.match(/销售方名称\s*:\s*(.+)/)
        if (issuerMatch) {
          parsedData.issuer = issuerMatch[1].trim()
        }

        // 解析购买方名称（收票方）
        const recipientMatch = ocrText.match(/购买方名称\s*:\s*(.+)/)
        if (recipientMatch) {
          parsedData.recipient = recipientMatch[1].trim()
        }

        // 解析价税合计（金额）
        const amountMatch = ocrText.match(/价税合计\s*:\s*¥?([\d,]+\.?\d*)/)
        if (amountMatch) {
          const amountStr = amountMatch[1].replace(/,/g, '')
          parsedData.amount = parseFloat(amountStr) || 0
        }

        // 解析税率
        const taxRateMatch = ocrText.match(/税率\s*:\s*(\d+(?:\.\d+)?)%/)
        if (taxRateMatch) {
          parsedData.taxRate = parseFloat(taxRateMatch[1]) || 0
        }

        // 解析税额
        const taxAmountMatch = ocrText.match(/税额\s*:\s*¥?([\d,]+\.?\d*)/)
        if (taxAmountMatch) {
          const taxAmountStr = taxAmountMatch[1].replace(/,/g, '')
          parsedData.totalTaxAmount = parseFloat(taxAmountStr) || 0
        }

        // 解析价税合计大写
        const amountInWordsMatch = ocrText.match(/价税合计大写\s*:\s*(.+)/)
        if (amountInWordsMatch) {
          parsedData.amountInWords = amountInWordsMatch[1].trim()
        }

        // 解析货物或应税劳务、服务名称作为摘要
        const summaryMatch = ocrText.match(/货物或应税劳务、服务名称\s*:\s*(.+)/)
        if (summaryMatch) {
          parsedData.summary = summaryMatch[1].trim()
        }

        console.log('解析OCR识别信息成功:', parsedData)

      } catch (error) {
        console.error('解析OCR识别信息时出错:', error)
      }

      return parsedData
    },

    /**
     * 标准化票据类型
     * 将各种OCR识别返回的票据类型格式映射到表单选项中的标准格式
     */
    standardizeInvoiceType(originalType) {
      if (!originalType) return null

      const typeStr = String(originalType).toLowerCase()

      // 优先处理完整的电子发票格式（包含增值税字样的）
      if (typeStr.includes('电子发票') && typeStr.includes('增值税') && typeStr.includes('专用发票')) {
        return '电子发票(专用发票)'
      } else if (typeStr.includes('电子发票') && typeStr.includes('增值税') && typeStr.includes('普通发票')) {
        return '电子发票(普通发票)'
      }

      // 处理带括号的电子发票格式
      if (typeStr.includes('电子发票(增值税专用发票)') || typeStr.includes('电子发票（增值税专用发票）')) {
        return '电子发票(专用发票)'
      } else if (typeStr.includes('电子发票(增值税普通发票)') || typeStr.includes('电子发票（增值税普通发票）')) {
        return '电子发票(普通发票)'
      }

      // 处理简化的电子发票格式
      if (typeStr.includes('电子发票') && typeStr.includes('专用发票')) {
        return '电子发票(专用发票)'
      } else if (typeStr.includes('电子发票') && typeStr.includes('普通发票')) {
        return '电子发票(普通发票)'
      }

      // 补充常见简称：专票/普票（含是否电子）
      if (typeStr.includes('电子') && typeStr.includes('专票')) {
        return '电子发票(专用发票)'
      } else if (typeStr.includes('电子') && typeStr.includes('普票')) {
        return '电子发票(普通发票)'
      } else if (typeStr.includes('专票')) {
        return '增值税专用发票'
      } else if (typeStr.includes('普票')) {
        return '增值税普通发票'
      }

      // 处理传统发票格式
      if (typeStr.includes('专用发票')) {
        return '增值税专用发票'
      } else if (typeStr.includes('普通发票')) {
        return '增值税普通发票'
      }

      // 处理其他票据类型
      if (typeStr.includes('火车票') || typeStr.includes('铁路') || typeStr.includes('客票')) {
        return '火车票'
      } else if (typeStr.includes('机票') || typeStr.includes('航班') || typeStr.includes('行程单')) {
        return '机票行程单'
      } else if (typeStr.includes('出租车') || typeStr.includes('taxi')) {
        return '出租车票'
      } else if (typeStr.includes('收据')) {
        return '收据'
      } else if (typeStr.includes('发票')) {
        return '增值税普通发票' // 默认为普通发票
      }

      return null
    }
  }
}
</script>

<style scoped>
@import "../../styles/form-layout.css";

.mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 999;
}
</style>

